import React, { useEffect, useState, useCallback } from "react";
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronRight } from "react-icons/fi";
import { taskAssignmentsApi, TASK_STATUSES, TASK_PRIORITIES, statusLabel } from "./taskAssignmentsApi";
import TaskProgressPanel from "./TaskProgressPanel";
import { showSuccessToast, showErrorToast } from "../../constants/ToastService";
import "./TaskAssignments.css";

const today = () => new Date().toISOString().split("T")[0];

const emptyForm = {
    title: "",
    assigned_to: "",
    start_date: today(),
    end_date: today(),
    description: "",
    priority: "MEDIUM",
    status: "TODO",
};

const TaskAssignments = () => {
    const [tasks, setTasks] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    const [filters, setFilters] = useState({ status: "", assigned_to: "", search: "" });

    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const loadTasks = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.assigned_to) params.assigned_to = filters.assigned_to;
            if (filters.search) params.search = filters.search;
            const res = await taskAssignmentsApi.getAssignments(params);
            setTasks(res.data || []);
        } catch (err) {
            showErrorToast(err.message || "Failed to load tasks");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    useEffect(() => {
        taskAssignmentsApi
            .getEmployees()
            .then((res) => setEmployees(res.data || []))
            .catch(() => setEmployees([]));
    }, []);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = (task) => {
        setEditingId(task.id);
        setForm({
            title: task.title || "",
            assigned_to: task.assigned_to || "",
            start_date: task.start_date || today(),
            end_date: task.end_date || today(),
            description: task.description || "",
            priority: task.priority || "MEDIUM",
            status: task.status || "TODO",
        });
        setShowModal(true);
    };

    const handleFormChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.assigned_to || !form.start_date || !form.end_date) {
            showErrorToast("Title, Assignee, Start Date and End Date are required");
            return;
        }
        if (form.end_date < form.start_date) {
            showErrorToast("End Date cannot be before Start Date");
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await taskAssignmentsApi.updateAssignment({ id: editingId, ...form });
                showSuccessToast("Task updated");
            } else {
                await taskAssignmentsApi.createAssignment(form);
                showSuccessToast("Task assigned");
            }
            setShowModal(false);
            loadTasks();
        } catch (err) {
            showErrorToast(err.message || "Failed to save task");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (task) => {
        if (!window.confirm(`Delete task "${task.title}"?`)) return;
        try {
            await taskAssignmentsApi.deleteAssignment(task.id);
            showSuccessToast("Task deleted");
            loadTasks();
        } catch (err) {
            showErrorToast(err.message || "Failed to delete task");
        }
    };

    const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

    return (
        <div className="ta-page">
            <div className="ta-header">
                <div>
                    <h1 className="ta-title">Task Assignment</h1>
                    <p className="ta-subtitle">Assign tasks to users with start &amp; end dates and track completion.</p>
                </div>
                <button className="ta-btn ta-btn--primary" onClick={openCreate}>
                    <FiPlus size={14} /> Assign Task
                </button>
            </div>

            <div className="ta-filters">
                <input
                    type="text"
                    placeholder="Search title / code…"
                    value={filters.search}
                    onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                />
                <select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
                    <option value="">All Statuses</option>
                    {TASK_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                </select>
                <select value={filters.assigned_to} onChange={(e) => setFilters((p) => ({ ...p, assigned_to: e.target.value }))}>
                    <option value="">All Assignees</option>
                    {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <p className="ta-muted">Loading tasks…</p>
            ) : tasks.length === 0 ? (
                <p className="ta-muted">No tasks yet. Click “Assign Task” to create one.</p>
            ) : (
                <div className="ta-table">
                    <div className="ta-table__head">
                        <span />
                        <span>Code</span>
                        <span>Title</span>
                        <span>Assignee</span>
                        <span>Start</span>
                        <span>End</span>
                        <span>Status</span>
                        <span>Progress</span>
                        <span>Actions</span>
                    </div>
                    {tasks.map((task) => (
                        <div key={task.id} className="ta-row-group">
                            <div className="ta-row">
                                <button className="ta-expand-btn" onClick={() => toggleExpand(task.id)}>
                                    {expandedId === task.id ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                                </button>
                                <span className="ta-code">{task.task_code}</span>
                                <span className="ta-cell-title">{task.title}</span>
                                <span>{task.assigned_to_name || "—"}</span>
                                <span>{task.start_date}</span>
                                <span>{task.end_date}</span>
                                <span>
                                    <span className={`ta-status ta-status--${task.status}`}>{statusLabel(task.status)}</span>
                                </span>
                                <span className="ta-progress-cell">
                                    <span className="ta-bar"><span className="ta-bar__fill" style={{ width: `${task.completion_percentage || 0}%` }} /></span>
                                    <span className="ta-pct">{task.completion_percentage || 0}%</span>
                                </span>
                                <span className="ta-actions">
                                    <button className="ta-icon-btn" title="Edit" onClick={() => openEdit(task)}><FiEdit2 size={14} /></button>
                                    <button className="ta-icon-btn ta-icon-btn--danger" title="Delete" onClick={() => handleDelete(task)}><FiTrash2 size={14} /></button>
                                </span>
                            </div>
                            {expandedId === task.id && (
                                <div className="ta-accordion">
                                    {task.description && <p className="ta-desc">{task.description}</p>}
                                    <TaskProgressPanel task={task} canEdit={true} onUpdated={loadTasks} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="ta-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="ta-modal" onClick={(e) => e.stopPropagation()}>
                        <h2 className="ta-modal__title">{editingId ? "Edit Task" : "Assign Task"}</h2>
                        <form onSubmit={handleSubmit} className="ta-form">
                            <label>
                                Title <span className="ta-required">*</span>
                                <input type="text" value={form.title} onChange={(e) => handleFormChange("title", e.target.value)} required />
                            </label>
                            <label>
                                Assignee <span className="ta-required">*</span>
                                <select value={form.assigned_to} onChange={(e) => handleFormChange("assigned_to", e.target.value)} required>
                                    <option value="" disabled hidden>Select a user</option>
                                    {employees.map((emp) => (
                                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="ta-form__row">
                                <label>
                                    Start Date <span className="ta-required">*</span>
                                    <input type="date" value={form.start_date} onChange={(e) => handleFormChange("start_date", e.target.value)} required />
                                </label>
                                <label>
                                    End Date <span className="ta-required">*</span>
                                    <input type="date" value={form.end_date} min={form.start_date} onChange={(e) => handleFormChange("end_date", e.target.value)} required />
                                </label>
                            </div>
                            <div className="ta-form__row">
                                <label>
                                    Priority
                                    <select value={form.priority} onChange={(e) => handleFormChange("priority", e.target.value)}>
                                        {TASK_PRIORITIES.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    Status
                                    <select value={form.status} onChange={(e) => handleFormChange("status", e.target.value)}>
                                        {TASK_STATUSES.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                            <label>
                                Description
                                <textarea rows={3} value={form.description} onChange={(e) => handleFormChange("description", e.target.value)} />
                            </label>
                            <div className="ta-form__actions">
                                <button type="button" className="ta-btn" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="ta-btn ta-btn--primary" disabled={saving}>
                                    {saving ? "Saving…" : editingId ? "Save Changes" : "Assign"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskAssignments;
